import { tableClassNames, TableInner } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';
import { useFilter } from '../../contexts/filters';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { useGiftReport } from '../../entities/reports/queries';
import { TCustomReportEntity, TGiftsReportRow } from '../../generated/graphql';
import { ReportActions } from './ReportActions';
import { companyFilter, monthFilter } from './reportFilters';

import './ReportPage.scss';

const EMPLOYEE_BIRTHDAY_REPORT_TYPE = 'employeeBirthday';
const DEPENDANT_BIRTHDAY_REPORT_TYPE = 'dependantBirthday';

const REPORT_GIFT_TYPE_LABELS = {
  newHire: 'New Hire',
  workAnniversaryDeluxe: 'Work Ann. Deluxe',
  workAnniversaryMini: 'Work Ann. Mini',
  workAnniversaryBrickOnly: 'Work Ann. Brick Only',
  workAnniversaryCustom: 'Work Ann. Custom',
  workAnniversaryLegoOnly: 'Work Ann. LEGO Only',
  [EMPLOYEE_BIRTHDAY_REPORT_TYPE]: 'Employee Birthday',
  [DEPENDANT_BIRTHDAY_REPORT_TYPE]: 'Dependant Birthday',
  babyGift: 'Baby',
  bereavement: 'Bereavement',
  wedding: 'Wedding',
  replacementWorkAnniversary: 'Replacement Work Ann.',
  replacementEmployeeBirthday: 'Replacement Employee B-day',
  carePackage: 'Care Packages',
  customBricks: 'Custom Bricks',
  other: 'Other'
} as const;

type TReportGiftType = keyof typeof REPORT_GIFT_TYPE_LABELS;

const REPORT_GIFT_TYPES = Object.keys(REPORT_GIFT_TYPE_LABELS) as TReportGiftType[];

type TRow = TGiftsReportRow;

const REPORT_HEADERS = ['Company ID', 'Company Name', ...REPORT_GIFT_TYPES.map((type) => REPORT_GIFT_TYPE_LABELS[type]), 'TOTAL:'];

export const GiftReports = () => {
  const { filtersPageMode } = useFilter();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [companyId, setCompanyId] = useState<string>();

  useEffect(() => {
    filtersPageMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: companies, error: companiesError, loading: companiesLoading } = useQueryListAllCompaniesOptions();

  const { data: rows, error: reportError, loading: reportLoading } = useGiftReport(month, companyId);

  const activeCompanies = useMemo(() => companies.filter((company) => company.active), [companies]);

  const sheet = useMemo(() => {
    return [['MONTH:', month], REPORT_HEADERS, ...rows.map((row) => [row.companyId, row.companyName, ...REPORT_GIFT_TYPES.map((type) => row[type]), row.total])];
  }, [month, rows]);

  const filters = useMemo<TFiltersProps['filters']>(() => [monthFilter(month, setMonth), companyFilter(activeCompanies, companyId, setCompanyId)], [month, activeCompanies, companyId]);

  const loading = reportLoading || companiesLoading;
  const error = reportError || companiesError;

  return (
    <TablePage dataLoading={loading} error={error} title='Gifts (monthly)'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TRow>
          enableSorting={true}
          defaultSorting={{ id: 'companyName', desc: false }}
          columns={[
            {
              // Undefined sort values keep the total last in either sort direction.
              accessorKey: 'companyId',
              accessorFn: (row) => (row.isTotal ? undefined : row.companyId),
              sortUndefined: 'first',
              cell: ({ row }) => (
                <div className={clsx(tableClassNames.tertiary, 'uuid-label')} title={row.original.companyId}>
                  {row.original.companyId}
                </div>
              ),
              header: 'Company ID'
            },
            {
              accessorKey: 'companyName',
              accessorFn: (row) => (row.isTotal ? undefined : row.companyName),
              sortUndefined: 'first',
              cell: ({ row }) => <div className={clsx(tableClassNames.primary)}>{row.original.companyName}</div>,
              header: 'Company Name'
            },
            ...REPORT_GIFT_TYPES.map((type) => ({
              accessorKey: type,
              accessorFn: (row: TRow) => (row.isTotal ? undefined : row[type]),
              sortUndefined: 'first' as const,
              cell: ({ row }: { row: { original: TRow } }) => <div className={clsx(tableClassNames.quaternary)}>{row.original[type]}</div>,
              header: REPORT_GIFT_TYPE_LABELS[type]
            })),
            {
              accessorKey: 'total',
              accessorFn: (row) => (row.isTotal ? undefined : row.total),
              sortUndefined: 'first',
              cell: ({ row }) => <div className={clsx(tableClassNames.quaternary)}>{row.original.total}</div>,
              header: 'TOTAL:'
            }
          ]}
          data={rows}
          emptyText='No gifts were found.'
          expandable={false}
          fillSelectedRows={false}
          fixedHeader={true}
          getRowId={(row, index) => (row.isTotal ? 'total' : row.companyId || index.toString())}
          hideSubRowsSelect={true}
          hoverableRows={true}
          selectableRows={false}
          virtual={false}
        />
      </div>
      <ReportActions data={sheet} disabled={loading || !!error || rows.length === 0} filename={`cs-gift-report_${month}`} customReportEntity={TCustomReportEntity.Gifts} />
    </TablePage>
  );
};
